import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Phone, MessageCircle, CheckCircle2, XCircle, Truck, PackageCheck,
  ChevronDown, ChevronRight, Copy, RefreshCw, Users, IndianRupee,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { formatRupee as fmt } from '@/lib/format';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmtWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const TABS = [
  { key: 'new', label: 'New', icon: RefreshCw, color: 'amber' },
  { key: 'confirmed', label: 'Confirmed', icon: CheckCircle2, color: 'lime' },
  { key: 'declined', label: 'Not Confirmed', icon: XCircle, color: 'red' },
  { key: 'dispatched', label: 'Dispatched', icon: Truck, color: 'blue' },
  { key: 'delivered', label: 'Delivered', icon: PackageCheck, color: 'stone' },
];

export default function StoreOrders() {
  const [tab, setTab] = useState('new');
  const [counts, setCounts] = useState({});
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [confirmDialog, setConfirmDialog] = useState(null); // {order}
  const [declineDialog, setDeclineDialog] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadCounts = useCallback(() => {
    axios.get(`${BACKEND_URL}/api/admin/store-orders/counts`, { withCredentials: true })
      .then(r => setCounts(r.data || {}))
      .catch(() => {});
  }, []);

  const loadGroups = useCallback((activeTab) => {
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/admin/store-orders?tab=${activeTab}`, { withCredentials: true })
      .then(r => setGroups(r.data || []))
      .catch(e => toast.error(e?.response?.data?.detail || 'Failed to load online orders'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadCounts(); }, [loadCounts]);
  useEffect(() => { loadGroups(tab); }, [tab, loadGroups]);

  const refresh = () => { loadCounts(); loadGroups(tab); };

  const toggleExpand = (key) => setExpanded(p => ({ ...p, [key]: !p[key] }));

  const doDispatch = async (orderIds) => {
    if (!orderIds.length) return;
    setBusy(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/store-orders/dispatch`, { order_ids: orderIds }, { withCredentials: true });
      toast.success(`Dispatched ${orderIds.length} order${orderIds.length === 1 ? '' : 's'}`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Dispatch failed');
    } finally {
      setBusy(false);
    }
  };

  const doDeliver = async (orderIds) => {
    if (!orderIds.length) return;
    setBusy(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/store-orders/deliver`, { order_ids: orderIds }, { withCredentials: true });
      toast.success(`Marked ${orderIds.length} delivered`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delivery update failed');
    } finally {
      setBusy(false);
    }
  };

  const totalOrdersInTab = useMemo(() => groups.reduce((s, g) => s + g.order_count, 0), [groups]);

  return (
    <div className="p-6 space-y-4" data-testid="store-orders-page">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-stone-900">Online Orders</h1>
          <p className="text-sm text-stone-500 mt-1">
            One inbox for everything from <b>nirmaanstore.com</b>. Cards are grouped by customer so multiple orders can be handled together.
          </p>
        </div>
        <Button variant="outline" onClick={refresh} disabled={loading} data-testid="refresh-btn">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 border-b border-stone-200">
        {TABS.map(t => {
          const Icon = t.icon;
          const active = tab === t.key;
          const count = counts[t.key] || 0;
          return (
            <button
              key={t.key}
              data-testid={`tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm border-b-2 -mb-px transition ${active ? `border-amber-700 text-amber-800 font-semibold` : 'border-transparent text-stone-500 hover:text-stone-800'}`}
            >
              <Icon className="w-4 h-4" /> {t.label}
              {count > 0 && (
                <span className={`ml-1 rounded-full text-[10px] font-semibold px-1.5 py-0.5 min-w-[18px] text-center ${active ? 'bg-amber-100 text-amber-800' : 'bg-stone-100 text-stone-600'}`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="p-12 flex items-center justify-center text-stone-500 gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : groups.length === 0 ? (
        <div className="p-16 text-center border border-dashed border-stone-200 rounded-xl">
          <div className="text-stone-400 mb-2">No orders in <b>{TABS.find(t => t.key === tab)?.label}</b>.</div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="store-orders-list">
          <div className="text-xs text-stone-500">{groups.length} customer{groups.length === 1 ? '' : 's'} · {totalOrdersInTab} order{totalOrdersInTab === 1 ? '' : 's'}</div>
          {groups.map((g, gi) => {
            const key = g.customer.customer_id || `${gi}`;
            const isExpanded = expanded[key];
            const canBatchDispatch = tab === 'confirmed';
            const canBatchDeliver = tab === 'dispatched';
            const orderIds = g.orders.map(o => o.order_id);
            const phoneClean = (g.customer.phone || '').replace(/[^+\d]/g, '');
            return (
              <div key={key} className="border border-stone-200 rounded-xl bg-white overflow-hidden" data-testid={`customer-card-${key}`}>
                <div className="p-4 flex flex-wrap items-start justify-between gap-3 cursor-pointer hover:bg-stone-50" onClick={() => toggleExpand(key)}>
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-800 flex items-center justify-center font-semibold flex-shrink-0">
                      {(g.customer.name || '?').charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="font-semibold text-stone-900">{g.customer.name || 'Unknown customer'}</div>
                        {isExpanded ? <ChevronDown className="w-4 h-4 text-stone-400" /> : <ChevronRight className="w-4 h-4 text-stone-400" />}
                      </div>
                      <div className="text-xs text-stone-500 mt-0.5">{g.customer.phone} {g.customer.address && <>· {g.customer.address.split('\n')[0]}</>}</div>
                      <div className="text-[11px] text-stone-500 mt-1">
                        {g.order_count} order{g.order_count === 1 ? '' : 's'} · {g.item_count} item{g.item_count === 1 ? '' : 's'} · <span className="font-semibold text-stone-800">{fmt(g.value)}</span> · latest {fmtWhen(g.latest_at)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                    {phoneClean && <a href={`tel:${phoneClean}`} className="inline-flex items-center gap-1 border border-stone-300 hover:border-amber-700 text-stone-700 hover:text-amber-800 text-xs rounded px-2 py-1"><Phone className="w-3 h-3" /> Call</a>}
                    {phoneClean && <a href={`https://wa.me/${phoneClean.replace(/^\+/, '')}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 border border-stone-300 hover:border-lime-700 text-stone-700 hover:text-lime-800 text-xs rounded px-2 py-1"><MessageCircle className="w-3 h-3" /> WhatsApp</a>}
                    {canBatchDispatch && g.order_count > 1 && (
                      <Button size="sm" className="bg-blue-700 hover:bg-blue-800 text-white h-7 text-xs" onClick={() => doDispatch(orderIds)} disabled={busy} data-testid={`batch-dispatch-${key}`}>
                        <Truck className="w-3 h-3 mr-1" /> Batch dispatch ({g.order_count})
                      </Button>
                    )}
                    {canBatchDeliver && g.order_count > 1 && (
                      <Button size="sm" className="bg-stone-800 hover:bg-black text-white h-7 text-xs" onClick={() => doDeliver(orderIds)} disabled={busy} data-testid={`batch-deliver-${key}`}>
                        <PackageCheck className="w-3 h-3 mr-1" /> Batch deliver ({g.order_count})
                      </Button>
                    )}
                  </div>
                </div>
                {isExpanded && (
                  <div className="border-t border-stone-200 divide-y divide-stone-100 bg-stone-50/50">
                    {g.orders.map(order => (
                      <OrderRow
                        key={order.order_id}
                        order={order}
                        tab={tab}
                        busy={busy}
                        onConfirm={() => setConfirmDialog({ order })}
                        onDecline={() => setDeclineDialog({ order })}
                        onDispatch={() => doDispatch([order.order_id])}
                        onDeliver={() => doDeliver([order.order_id])}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {confirmDialog && (
        <ConfirmDialog
          order={confirmDialog.order}
          onClose={() => setConfirmDialog(null)}
          onDone={() => { setConfirmDialog(null); refresh(); }}
        />
      )}
      {declineDialog && (
        <DeclineDialog
          order={declineDialog.order}
          onClose={() => setDeclineDialog(null)}
          onDone={() => { setDeclineDialog(null); refresh(); }}
        />
      )}
    </div>
  );
}

function OrderRow({ order, tab, busy, onConfirm, onDecline, onDispatch, onDeliver }) {
  return (
    <div className="p-4 space-y-2" data-testid={`order-row-${order.order_id}`}>
      <div className="flex flex-wrap items-baseline gap-2 justify-between">
        <div className="text-xs text-stone-500 font-mono">{order.order_id} · placed {fmtWhen(order.created_at)}</div>
        <div className="flex items-center gap-2 flex-wrap">
          {tab === 'new' && (
            <>
              <Button size="sm" className="bg-lime-700 hover:bg-lime-800 text-white h-7 text-xs" onClick={onConfirm} disabled={busy} data-testid={`confirm-${order.order_id}`}>
                <CheckCircle2 className="w-3 h-3 mr-1" /> Confirm
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-xs text-red-700 border-red-300 hover:bg-red-50" onClick={onDecline} disabled={busy} data-testid={`decline-${order.order_id}`}>
                <XCircle className="w-3 h-3 mr-1" /> Not confirmed
              </Button>
            </>
          )}
          {tab === 'confirmed' && (
            <>
              <Button size="sm" className="bg-blue-700 hover:bg-blue-800 text-white h-7 text-xs" onClick={onDispatch} disabled={busy} data-testid={`dispatch-${order.order_id}`}>
                <Truck className="w-3 h-3 mr-1" /> Dispatch
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-xs text-red-700 border-red-300 hover:bg-red-50" onClick={onDecline} disabled={busy} data-testid={`decline-c-${order.order_id}`}>
                Cancel
              </Button>
            </>
          )}
          {tab === 'dispatched' && (
            <Button size="sm" className="bg-stone-800 hover:bg-black text-white h-7 text-xs" onClick={onDeliver} disabled={busy} data-testid={`deliver-${order.order_id}`}>
              <PackageCheck className="w-3 h-3 mr-1" /> Mark delivered
            </Button>
          )}
        </div>
      </div>
      <div className="grid gap-1 text-sm">
        {order.items.map((it, i) => (
          <div key={i} className="flex justify-between gap-2 text-stone-700">
            <span className="truncate"><span className="font-medium text-stone-900">{it.quantity}× {it.product_name}</span> <span className="text-[11px] text-stone-500">{[it.product_brand, it.product_size, it.product_color].filter(x => x && x !== 'NA').join(' · ')}</span></span>
            <span className="tabular-nums text-stone-600 flex-shrink-0">{fmt(it.line_total)}</span>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap justify-between items-baseline text-xs pt-2 border-t border-stone-100">
        <div className="text-stone-500 space-x-3">
          {order.confirmed_at && <span>Confirmed {fmtWhen(order.confirmed_at)}</span>}
          {order.payment_mode && <span>· Payment: {order.payment_mode}{order.payment_reference && ` (${order.payment_reference})`}</span>}
          {order.delivery_charges > 0 && <span>· Delivery: {fmt(order.delivery_charges)}</span>}
          {order.decline_reason && <span className="text-red-700">Reason: {order.decline_reason}</span>}
          {order.dispatched_at && <span>Dispatched {fmtWhen(order.dispatched_at)}</span>}
        </div>
        <div className="font-semibold text-stone-900 tabular-nums">Total: {fmt(order.total_amount)}</div>
      </div>
    </div>
  );
}

const PAYMENT_MODES = ['COD (Cash on Delivery)', 'UPI', 'Bank Transfer', 'Card', 'Cheque', 'Other'];

function ConfirmDialog({ order, onClose, onDone }) {
  const [deliveryCharges, setDeliveryCharges] = useState(order.delivery_charges || 0);
  const [paymentMode, setPaymentMode] = useState(order.payment_mode || 'COD (Cash on Delivery)');
  const [reference, setReference] = useState(order.payment_reference || '');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!paymentMode) { toast.error('Please pick a payment mode'); return; }
    if (Number(deliveryCharges) < 0) { toast.error('Delivery charges cannot be negative'); return; }
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/store-orders/${order.order_id}/confirm`, {
        delivery_charges: Number(deliveryCharges || 0),
        payment_mode: paymentMode,
        payment_reference: reference,
      }, { withCredentials: true });
      toast.success('Order confirmed');
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Confirm failed');
    } finally {
      setSaving(false);
    }
  };

  const newTotal = Number(order.total_amount || 0) + Number(deliveryCharges || 0);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white sm:max-w-md" data-testid="confirm-dialog">
        <DialogHeader>
          <DialogTitle>Confirm order · {order.order_id}</DialogTitle>
        </DialogHeader>
        <div className="text-sm text-stone-600 mb-3">
          Enter what was agreed on the call. This gets stamped onto the order and shown on the customer&apos;s tracking page.
        </div>
        <div className="space-y-4">
          <div>
            <Label className="text-xs">Delivery charges</Label>
            <div className="mt-1 flex items-center gap-2">
              <IndianRupee className="w-4 h-4 text-stone-400" />
              <Input data-testid="confirm-delivery" type="number" value={deliveryCharges} onChange={e => setDeliveryCharges(e.target.value)} min="0" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Payment mode</Label>
            <Select value={paymentMode} onValueChange={setPaymentMode}>
              <SelectTrigger data-testid="confirm-mode" className="mt-1"><SelectValue placeholder="Select mode" /></SelectTrigger>
              <SelectContent className="bg-white">
                {PAYMENT_MODES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Payment reference (optional)</Label>
            <Input data-testid="confirm-ref" value={reference} onChange={e => setReference(e.target.value)} placeholder="Txn ID, UTR, cheque no…" className="mt-1" />
          </div>
          <div className="bg-stone-50 rounded p-3 text-sm space-y-1">
            <div className="flex justify-between"><span className="text-stone-600">Items subtotal</span><span className="tabular-nums">{fmt(order.total_amount - (order.delivery_charges || 0))}</span></div>
            <div className="flex justify-between"><span className="text-stone-600">Delivery</span><span className="tabular-nums">{fmt(deliveryCharges)}</span></div>
            <div className="flex justify-between font-semibold pt-1 border-t border-stone-200"><span>New total</span><span className="tabular-nums">{fmt(newTotal)}</span></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-lime-700 hover:bg-lime-800 text-white" data-testid="confirm-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm order'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeclineDialog({ order, onClose, onDone }) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!reason.trim()) { toast.error('Please give a short reason'); return; }
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/store-orders/${order.order_id}/decline`, { reason }, { withCredentials: true });
      toast.success('Marked as not confirmed');
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white sm:max-w-md" data-testid="decline-dialog">
        <DialogHeader>
          <DialogTitle>Mark not confirmed · {order.order_id}</DialogTitle>
        </DialogHeader>
        <div className="text-sm text-stone-600 mb-3">
          Customer didn&apos;t confirm during the call. Give a short reason (we track this so we can improve). Items are also cancelled so vendors stop seeing them.
        </div>
        <Textarea data-testid="decline-reason" value={reason} onChange={e => setReason(e.target.value)} rows={3} placeholder="Customer changed mind / price too high / duplicate order / …" />
        <DialogFooter className="mt-3">
          <Button variant="outline" onClick={onClose}>Back</Button>
          <Button onClick={submit} disabled={saving} className="bg-red-700 hover:bg-red-800 text-white" data-testid="decline-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Mark not confirmed'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
